import type {
  EchoflicksSessionJourney,
  FinalVideoDelivery,
  FinalVideoDeliveryReconciliation,
} from "../types";

export type FinalVideoDeliveryModel = {
  status: string;
  channel: string | null;
  method: string | null;
  reconciliationRequired: boolean;
  duplicateSuppressedCount: number;
  reconciliations: FinalVideoDeliveryReconciliation[];
  updatedAt: string | null;
  deliveries: FinalVideoDelivery[];
};

const STATUS_RANK: Record<string, number> = {
  created: 0,
  delivery_pending: 0,
  queued: 1,
  claimed: 2,
  submitting: 3,
  submitted: 3,
  retryable_failed: 3,
  failed: 4,
  outcome_unknown: 4,
  delivered: 5,
};

function time(value: FinalVideoDelivery) {
  return (
    value.lifecycle_updated_at ?? value.updated_at ?? value.occurred_at ?? ""
  );
}

/** Merge at-least-once rows by durable delivery id and derive one operator view. */
export function buildFinalVideoDeliveryModel(
  journey: EchoflicksSessionJourney | null | undefined,
): FinalVideoDeliveryModel {
  const byId = new Map<string, FinalVideoDelivery>();
  for (const delivery of journey?.final_video_deliveries ?? []) {
    const existing = byId.get(delivery.delivery_id);
    if (!existing || time(delivery) >= time(existing)) {
      byId.set(delivery.delivery_id, { ...existing, ...delivery });
    }
  }
  const deliveries = [...byId.values()].sort(
    (left, right) =>
      (STATUS_RANK[right.status ?? ""] ?? -1) -
        (STATUS_RANK[left.status ?? ""] ?? -1) ||
      time(right).localeCompare(time(left)) ||
      left.delivery_id.localeCompare(right.delivery_id),
  );
  const selected = deliveries[0];
  const reconciliations = deliveries
    .flatMap((delivery) => delivery.reconciliation_history ?? [])
    .sort(
      (left, right) =>
        right.occurred_at.localeCompare(left.occurred_at) ||
        (left.event_id ?? "").localeCompare(right.event_id ?? ""),
    );
  const summary = journey?.summary;
  const status =
    selected?.status ??
    summary?.final_video_delivery_status ??
    (summary?.render_created_at || summary?.status === "delivery_pending"
      ? "delivery_pending"
      : "not_started");
  return {
    status,
    channel: selected?.channel ?? summary?.final_video_delivery_channel ?? null,
    method: selected?.method ?? summary?.final_video_delivery_method ?? null,
    reconciliationRequired: deliveries.some(
      (delivery) =>
        delivery.status === "outcome_unknown" &&
        delivery.reconciliation_required === true,
    ),
    duplicateSuppressedCount: deliveries.reduce(
      (total, delivery) => total + (delivery.duplicate_suppressed_count ?? 0),
      0,
    ),
    reconciliations,
    updatedAt:
      (selected ? time(selected) : "") ||
      summary?.final_video_delivery_updated_at ||
      summary?.render_created_at ||
      null,
    deliveries,
  };
}
